import { cloneDeep, get, omit } from 'lodash';
import { type IFramework } from '@/framework';
import { createTextContentsByArtText } from '@/layers/text-layer';
import { delPropsFlag } from '@/utils/layer-kit';
import {
    checkIsHorizontalMultiLine,
    extractBaseTextInfoFromTextLayer,
    getBaseTextStyle,
    getListTextPreservePadding,
    TextMeasurer,
} from '@/utils/text-kit';
import {
    TextAlignType,
    TextWritingType,
    type ILayerProps,
    type IShapeExtension,
    type ITextLayerProps,
} from '@/version-types';
import { EDIT_AREA_ID_SELECTOR } from '../constants';
import { TextEditDependency } from '../dependency';
import { getTextEditState } from '../states/text-edit-state';
import { ClipboardData, type AbilityDependency } from '../types';
import { cleanAndUnescapeHtml, extractTextContentsFromElement, RangeHelper, TextLayerClipboard } from '../utils';
import { type ITextEditAdapter } from './interface';

export class TextLayerAdapter implements ITextEditAdapter {
    private shapeExtension: IShapeExtension | null = null;

    onBeforeEnterEditing(_: IFramework, originLayer: ILayerProps): ILayerProps {
        const { shapeExtension } = originLayer.data as ITextLayerProps;
        this.shapeExtension = shapeExtension ? shapeExtension : null;

        return originLayer;
    }

    onAfterEnterEditing(framework: IFramework, originLayer: ILayerProps) {
        let nextLayer = originLayer;

        if (this.shapeExtension) {
            nextLayer = framework.getInstance(TextEditDependency).cancelShapeText(originLayer.id) as ILayerProps;
        }

        nextLayer = { ...nextLayer, __isOnTextEditing: true };

        framework.stage.addOrUpdate(nextLayer);
        framework.operate.setActiveLayer(nextLayer.id);

        return nextLayer;
    }

    private sendLayerDiff(framework: IFramework, layer: ILayerProps) {
        framework.history.realEdit.sendDiff({
            [layer.id]: {
                data: {
                    text: get(layer, 'data.text'),
                    fontFamily: get(layer, 'data.fontFamily'),
                    textContents: get(layer, 'data.textContents'),
                    textMark: get(layer, 'data.textMark') || delPropsFlag,
                },
                materialIds: {
                    'font-family': layer.materialIds?.['font-family'],
                    'text-mark': layer.materialIds?.['text-mark'],
                },
            },
        });
    }

    private deleteLayerWhenClearContent(framework: IFramework, layer: ILayerProps) {
        getTextEditState(framework.identity).reset();

        framework.getInstance(TextEditDependency).deleteLayer(layer);
    }

    async onExitEditing(framework: IFramework, originLayer: ILayerProps, latestLayer: ILayerProps | null) {
        // 当图层处于编辑态时被删除，latestLayer 为 null，此时不做后续处理
        if (!latestLayer) {
            this.shapeExtension = null;
            return;
        }

        let originLayerData = originLayer.data as ITextLayerProps;
        let latestLayerData = latestLayer.data as ITextLayerProps;

        // 退出编辑时若内容为空，则删除图层
        if (latestLayerData.text === '') {
            return this.deleteLayerWhenClearContent(framework, originLayer);
        }

        if (this.shapeExtension) {
            originLayerData = { ...originLayerData, shapeExtension: this.shapeExtension };
            latestLayerData = { ...latestLayerData, shapeExtension: this.shapeExtension };
        }

        const hasChanged = JSON.stringify(latestLayerData) !== JSON.stringify(originLayerData);

        let nextLayer: ILayerProps = { ...latestLayer, data: latestLayerData };

        // 无论是否 hasChanged，只要 shapeExtension 存在，重新调用 applyShapeText
        if (this.shapeExtension) {
            const hasText = latestLayerData.text.length > 0;

            if (hasText) {
                const layerWithShapeExtension = await framework
                    .getInstance(TextEditDependency)
                    .applyShapeText(latestLayer.id, this.shapeExtension);
                if (layerWithShapeExtension) nextLayer = layerWithShapeExtension;

                framework.stage.addOrUpdate(nextLayer);
            } else {
                // 移除 shapeExtension
                const withoutShapeExtensionLayerData = { ...latestLayerData, shapeExtension: null };
                nextLayer = { ...latestLayer, data: withoutShapeExtensionLayerData };
            }

            this.shapeExtension = null;
        }

        nextLayer = { ...nextLayer, __isOnTextEditing: false };

        framework.stage.addOrUpdate(nextLayer);

        if (hasChanged) {
            framework.history.write(omit(originLayer, '__isOnTextEditing'), omit(nextLayer, '__isOnTextEditing'));
        }
        // 对比编辑状态下的是否收到最新文本
        if (originLayer.__renderTextVersion !== nextLayer.__renderTextVersion) {
            this.sendLayerDiff(framework, nextLayer);
        }
        return nextLayer;
    }

    onUpdate(framework: IFramework, editArea: HTMLDivElement, layer: ILayerProps) {
        const layerData = get(layer, 'data', {}) as ITextLayerProps;

        const text = cleanAndUnescapeHtml(editArea.innerHTML);
        let textContents = (() => {
            if (layerData.advancedTextMaterial) {
                return createTextContentsByArtText(text, layerData);
            }
            return extractTextContentsFromElement(editArea, (layer.data as ITextLayerProps).fontFamily);
        })();

        const nextLayer = cloneDeep(layer);

        // step1. 设置 `modified` 标记
        let layerExtraInfo = get(layer, 'extraInfo', {}) as NonNullable<ILayerProps['extraInfo']>;

        // 存在 extraInfo 为空字符串的情况，需要将 extraInfo 转换为对象
        if (Object.prototype.toString.call(layerExtraInfo) !== '[object Object]') {
            console.log('TextLayerAdapter.onUpdate: extraInfo is not an object', layer);
            layerExtraInfo = {};
        }

        layerExtraInfo.modified = true;
        nextLayer.extraInfo = layerExtraInfo;

        // 若 extraInfo 包含 overrideColorInArtTextCase，则需要重置花字 textContents 中的 color
        if (layerExtraInfo && layerExtraInfo.overrideColorInArtTextCase) {
            textContents = textContents.map((item) => ({ ...item, color: layerExtraInfo.overrideColorInArtTextCase }));
        }

        // step2. 设置 `text` 和 `textContents`
        layerData.text = text;
        layerData.textContents = textContents;
        nextLayer.data = layerData;

        // step3. 根据输入内容设置文本图层 layout
        const { writingMode } = layerData;
        const isVertical = writingMode !== TextWritingType.HORIZONTAL_TB;
        const fixedDirectionSize = layer.__fixedDirectionSize;
        const unsetFixedDirectionSize = typeof fixedDirectionSize === 'undefined';

        let keepConstantWidth = false;
        let keepConstantHeight = false;
        if (isVertical) {
            keepConstantHeight = unsetFixedDirectionSize ? false : true;
        } else {
            keepConstantWidth =
                Boolean(fixedDirectionSize) ||
                checkIsHorizontalMultiLine(layer.height, layerData.fontSize, layerData.lineSpacing);
        }

        const measureOptions = {
            keepConstantWidth,
            keepConstantHeight,
        };
        let textSize = TextMeasurer.measure(nextLayer, measureOptions);

        // ATTENTION: 检查是否超出舞台宽度，若超出则需要重新以舞台宽度为图层宽度进行尺寸计算
        const stageIdx = framework.stage.getActiveStageIndex();
        const { width: stageWidth, height: stageHeight } = framework.getSizeByIndex(stageIdx);

        if (!isVertical) {
            const willOverflow = textSize.width + nextLayer.left > stageWidth;
            if (willOverflow && unsetFixedDirectionSize) {
                textSize = TextMeasurer.measure(
                    { ...nextLayer, width: stageWidth },
                    { ...measureOptions, keepConstantWidth: true },
                );
                nextLayer.__fixedDirectionSize = true;
            }
        } else {
            const willOverflow = textSize.height + nextLayer.top > stageHeight;
            if (willOverflow && unsetFixedDirectionSize) {
                textSize = TextMeasurer.measure(
                    { ...nextLayer, height: stageHeight },
                    { ...measureOptions, keepConstantHeight: true },
                );
                nextLayer.__fixedDirectionSize = true;
            }
        }

        // step4.1 设置文本图层尺寸
        const layerWidth = layer.width;
        const layerHeight = layer.height;
        const nextLayerWidth =
            fixedDirectionSize && !isVertical ? Math.max(textSize.width, layerWidth) : textSize.width;
        const nextLayerHeight =
            fixedDirectionSize && isVertical ? Math.max(textSize.height, layerHeight) : textSize.height;
        nextLayer.width = nextLayerWidth;
        nextLayer.height = nextLayerHeight;

        // step4.2 设置文本图层定位
        const { textAlign } = layerData;
        if (!isVertical) {
            if (textAlign === TextAlignType.CENTER) {
                nextLayer.left = layer.left + (layerWidth - nextLayerWidth) / 2;
            } else if (textAlign === TextAlignType.RIGHT) {
                nextLayer.left = layer.left + (layerWidth - nextLayerWidth);
            }
        } else {
            nextLayer.left = layer.left + (layerWidth - nextLayerWidth);

            if (textAlign === TextAlignType.CENTER) {
                nextLayer.top = layer.top + (layerHeight - nextLayerHeight) / 2;
            } else if (textAlign === TextAlignType.RIGHT) {
                nextLayer.top = layer.top + (layerHeight - nextLayerHeight);
            }
        }

        // step5. 更新文本图层
        framework.stage.addOrUpdate(nextLayer);
        framework.operate.setActiveLayer(nextLayer.id);

        return nextLayer;
    }

    onPaste(framework: IFramework, layer: ILayerProps, data: ClipboardData) {
        const editArea = document.getElementById(EDIT_AREA_ID_SELECTOR);
        if (!editArea) return;

        const editingRange = RangeHelper.getEditingRange(editArea);
        if (!editingRange) return;

        return TextLayerClipboard.init(framework).paste(layer, data, editingRange);
    }

    onCopyOrCut(framework: IFramework, layer: ILayerProps) {
        const editArea = document.getElementById(EDIT_AREA_ID_SELECTOR);
        if (!editArea) return;

        const editingRange = RangeHelper.getEditingRange(editArea);
        if (!editingRange || editingRange.isCollapsed) return;

        return TextLayerClipboard.init(framework).copyOrCut(layer, editingRange);
    }

    onBlur(framework: IFramework, layer: ILayerProps) {
        const originLayer = getTextEditState(framework.identity).getOriginLayer();
        if (!originLayer) return;

        const originText = get(originLayer, 'data.text');
        const currentText = get(layer, 'data.text');

        // 若编辑内容为空，则删除图层
        if (currentText === '') {
            return this.deleteLayerWhenClearContent(framework, originLayer);
        }

        if (currentText !== originText) {
            framework.history.write(omit(originLayer, '__isOnTextEditing'), omit(layer, '__isOnTextEditing'));
        }
        // 对比编辑状态下的是否收到最新文本
        if (originLayer.__renderTextVersion !== layer.__renderTextVersion) {
            this.sendLayerDiff(framework, layer);
        }
    }

    getEditAreaStyle(_: IFramework, layer: ILayerProps) {
        const layerData = layer.data as ITextLayerProps;
        const { textContents = [] } = layerData;

        const baseTextInfo = extractBaseTextInfoFromTextLayer(layer);
        const baseStyle = getBaseTextStyle(baseTextInfo);
        const editAreaStyles: React.CSSProperties = {
            ...baseStyle,
            ...getListTextPreservePadding(baseTextInfo),
            color: 'transparent', // 文本图层在编辑态时，需要将交互层的文字隐藏，直接显示渲染层，防止文字重叠、特效无效正确展示的问题
            caretColor: baseStyle.color,
        };

        // 若存在分段文本，则需要删除外层部分样式，以分段内样式为准
        if (textContents.length > 0) {
            const droppedStyleProps = ['fontStyle', 'fontWeight', 'textDecoration'];
            droppedStyleProps.forEach((prop) => Reflect.deleteProperty(editAreaStyles, prop));
        }

        // 文字编辑层去除 text-size-adjust
        Reflect.deleteProperty(editAreaStyles, 'WebkitTextSizeAdjust');

        return editAreaStyles;
    }

    getEditAreaTextContent(_: IFramework, layer: ILayerProps) {
        const layerData = layer.data as ITextLayerProps;
        const { text, textContents = [] } = layerData;

        return {
            uniformText: text,
            richText: textContents,
        };
    }

    getEditAreaWrapperStyle() {
        return null;
    }

    useRendererDisplay() {
        return true;
    }

    getEnterEditingModeWays(): Array<'click' | 'doubleClick'> {
        return ['click', 'doubleClick'];
    }
}
